import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useToasts } from 'react-toast-notifications';
import T from 'i18n';
import MarkButton from './MarkButton';
import MarkTooltip from './MarkTooltip';
import html2canvas from 'html2canvas';
import { ViewerControl, Mark } from '@prizmdoc/viewer-core';
import { convert, cropCanvas, getScrollContainer } from '../utils';
import { CUSTOM_FLAG } from 'features/common/MaterialBasedComponents/Lookup';
import { useSelector } from 'react-redux';
import {
  selectCrossRef,
  selectCrossRefFontSize,
  selectCurrentCaseId,
  selectCurrentFile,
  selectCurrentSelectedFileMetaData,
  selectPermissions,
  selectPublicHyperlinks,
  selectQuery,
} from 'common/selectors';
import { LINK_COLOR, REGULAR_COLOR, SHARED_COLOR } from '../constants';
import { useAddAnnotation } from 'features/viewing/redux/addAnnotation';
import { useUpdateAnnotation } from 'features/viewing/redux/updateAnnotation';
import { useDeleteAnnotation } from 'features/viewing/redux/deleteAnnotation';

type NotionInterfaceProps = {
  viewerControl: typeof ViewerControl;
  isPresenting: boolean;
  markToBeCreated: typeof Mark;
  markToBeUpdated: typeof Mark;
  highlights: Array<any>;
  isTheatreMode: boolean;
  fileId?: string;
  isFileToCompare?: boolean;
  deleteMarkToBeCreated: (mark?: typeof Mark) => void;
  setMarkToBeUpdated: (mark: typeof Mark) => void;
  setMarkToBeCreated: (mark: typeof Mark) => void;
  hideTimeoutRef: React.MutableRefObject<any>;
  pauseCreateMarkHandlerRef: React.MutableRefObject<boolean>;
  userId: string;
  copyable?: boolean;
};

export default ({
  viewerControl,
  isPresenting,
  markToBeCreated,
  markToBeUpdated,
  highlights,
  fileId,
  isFileToCompare,
  deleteMarkToBeCreated,
  setMarkToBeUpdated,
  hideTimeoutRef,
  pauseCreateMarkHandlerRef,
  userId,
  copyable,
}: NotionInterfaceProps) => {
  const { addToast } = useToasts();
  const currentCaseId = useSelector(selectCurrentCaseId);
  const currentFileMetaData = useSelector(selectCurrentSelectedFileMetaData) as any;
  const queryParam = useSelector(selectQuery);
  const crossRefsEnabled = useSelector(selectCrossRef);
  const permissions = useSelector(selectPermissions);
  const currentFile = useSelector(selectCurrentFile);
  const publicHyperlinksEnabled = useSelector(selectPublicHyperlinks);
  const crossRefFontSize = useSelector(selectCrossRefFontSize);
  const [markHoveredOver, setMarkHoveredOver] = useState<any | null>(null);
  const [currentLinkData, setCurrentLinkData] = useState(null);

  const [textSelection, setTextSelection] = useState<any | null>(null);
  const [tooltipPoint, setTooltipPoint] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const previousPointRef = useRef<any>(null);
  const currentPointRef = useRef<any>(null);
  const timeoutIdRef = useRef<any>(null);

  const { addAnnotation: addAnnotationApi } = useAddAnnotation();
  const { updateAnnotation: updateAnnotationApi } = useUpdateAnnotation();
  const { deleteAnnotation: deleteAnnotationApi } = useDeleteAnnotation();

  const {
    documents: { canGeneratePublicHyperlinks, canCreateCrossRef },
  } = permissions;
  const canCreateAndEditCrossRefs = crossRefsEnabled && canCreateCrossRef(currentFile);
  const canCreateAndEditPublicHyperlinks =
    publicHyperlinksEnabled && canGeneratePublicHyperlinks(currentFile);
  const scrollContainer = getScrollContainer(viewerControl);

  //TEXT HIGHLIGHT
  const addHighlightHandler = (props: any) => {
    const { comment, tags } = props || {};

    try {
      pauseCreateMarkHandlerRef.current = true;

      const highLightMark = viewerControl.addMark(
        markToBeCreated.pageNumber,
        Mark.Type.HighlightAnnotation,
      );
      highLightMark.setPosition(markToBeCreated.position);
      highLightMark.setFillColor(REGULAR_COLOR);
      highLightMark.setData('comment', comment);
      highLightMark.interactionMode = Mark.InteractionMode.SelectionDisabled;
      highLightMark.opacity = 150;

      addAnnotationHandler(highLightMark, tags);
    } finally {
      setTimeout(() => {
        pauseCreateMarkHandlerRef.current = false;
      }, 500); // needed for acusoft to stop triggering events
    }
  };

  const addRedactionHandler = ({ reason }: any = {}) => {
    try {
      pauseCreateMarkHandlerRef.current = true;

      const highLightMark = viewerControl.addMark(
        markToBeCreated.pageNumber,
        Mark.Type.TextSelectionRedaction,
      );
      highLightMark.setPosition(markToBeCreated.position);
      highLightMark.setData('reason', reason);
      highLightMark.setReason(reason);
      highLightMark.interactionMode = Mark.InteractionMode.SelectionDisabled;

      addAnnotationHandler(highLightMark);
    } finally {
      setTimeout(() => {
        pauseCreateMarkHandlerRef.current = false;
      }, 500); // needed for acusoft to stop triggering events
    }
  };

  const updateHighlightHandler = ({ comment, tags }: any) => {
    const annotation = highlights.find((x: any) => x.annotation.uid === markToBeUpdated.uid);
    markToBeUpdated.setFillColor(
      annotation && annotation.createdBy && annotation.createdBy.id !== userId
        ? SHARED_COLOR
        : REGULAR_COLOR,
    );
    markToBeUpdated.setData('comment', comment);

    if (annotation) {
      try {
        // Verify mark is still attached to the document before serializing
        const allMarks = viewerControl.getAllMarks();
        const markExists = allMarks.some((m: any) => m.uid === markToBeUpdated.uid);

        if (!markExists) {
          return;
        }

        const serializationResult = viewerControl.serializeMarks([markToBeUpdated]);

        // Always wrap in Promise.resolve to ensure proper Promise chain
        return Promise.resolve(serializationResult)
          .then(([markObject]: any) => {
            if (markToBeUpdated) setMarkToBeUpdated(undefined);
            return updateAnnotationHandler(
              { ...annotation.annotation, ...markObject },
              annotation.id,
              tags,
              annotation.shared,
            );
          })
          .catch((error: any) => {
            console.warn('Error serializing mark for update:', error, {
              markId: markToBeUpdated.uid,
            });
            throw new Error('serialize failed');
          });
      } catch (error) {
        console.warn('Error in updateHighlightHandler:', error, { markId: markToBeUpdated?.uid });
      }
    }
  };

  const updateRedactionHandler = ({ reason }: any) => {
    const annotation = highlights.find((x: any) => x.annotation.uid === markToBeUpdated.uid);
    markToBeUpdated.setData('reason', reason);
    markToBeUpdated.setReason(reason);

    if (annotation) {
      try {
        // Verify mark is still attached to the document before serializing
        const allMarks = viewerControl.getAllMarks();
        const markExists = allMarks.some((m: any) => m.uid === markToBeUpdated.uid);

        if (!markExists) {
          return;
        }

        const serializationResult = viewerControl.serializeMarks([markToBeUpdated]);

        // Always wrap in Promise.resolve to ensure proper Promise chain
        Promise.resolve(serializationResult)
          .then(([markObject]: any) => {
            if (markToBeUpdated) setMarkToBeUpdated(undefined);
            updateAnnotationHandler({ ...annotation.annotation, ...markObject }, annotation.id);
          })
          .catch((error: any) => {
            console.warn('Error serializing mark for redaction update:', error, {
              markId: markToBeUpdated.uid,
            });
          });
      } catch (error) {
        console.warn('Error in updateRedactionHandler:', error, { markId: markToBeUpdated?.uid });
      }
    }
  };

  const addLinkHandler = ({ link, linkText }: any) => {
    setCurrentLinkData(link);
    try {
      pauseCreateMarkHandlerRef.current = true;

      const linkMark = viewerControl.addMark(
        markToBeCreated.pageNumber,
        Mark.Type.TextHyperlinkAnnotation,
      );

      const linkType = link?.linkedCaseId ? 'ExternalHyperlinkAnnotation' : link?.type;
      linkMark.setPosition(markToBeCreated.position);
      linkMark.setFillColor(LINK_COLOR);
      linkMark.setData('link', JSON.stringify(link));
      linkMark.setData('linkId', link.id);
      linkMark.setData('linkName', link.name);
      linkMark.setData('linkType', linkType);
      linkMark.setData('linkText', linkText);
      linkMark.interactionMode = Mark.InteractionMode.SelectionDisabled;
      linkMark.opacity = 150;
      linkMark.href = getHrefFromLink(link);
      addAnnotationHandler(linkMark, null);
    } finally {
      setTimeout(() => {
        pauseCreateMarkHandlerRef.current = false;
      }, 500); // needed for acusoft to stop triggering events
    }
  };

  const updateLinkHandler = ({ link, linkText, tags }: any) => {
    const linkType = link?.linkedCaseId ? 'ExternalHyperlinkAnnotation' : link?.type;
    markToBeUpdated.setFillColor(LINK_COLOR);
    markToBeUpdated.setData('link', JSON.stringify(link));
    markToBeUpdated.setData('linkId', link.id);
    markToBeUpdated.setData('linkName', link.name);
    markToBeUpdated.setData('linkType', linkType);
    markToBeUpdated.setData('linkText', linkText);
    markToBeUpdated.href = getHrefFromLink(link);

    const annotation = highlights.find((x: any) => x.annotation.uid === markToBeUpdated.uid);

    if (annotation) {
      try {
        // Verify mark is still attached to the document before serializing
        const allMarks = viewerControl.getAllMarks();
        const markExists = allMarks.some((m: any) => m.uid === markToBeUpdated.uid);

        if (!markExists) {
          return;
        }

        const serializationResult = viewerControl.serializeMarks([markToBeUpdated]);

        // Always wrap in Promise.resolve to ensure proper Promise chain
        Promise.resolve(serializationResult)
          .then(([markObject]: any) => {
            if (markToBeUpdated) setMarkToBeUpdated(undefined);
            updateAnnotationHandler(
              { ...annotation.annotation, ...markObject },
              annotation.id,
              tags,
              annotation.shared,
            );
          })
          .catch((error: any) => {
            console.warn('Error serializing mark for link update:', error, {
              markId: markToBeUpdated.uid,
            });
          });
      } catch (error) {
        console.warn('Error in updateLinkHandler:', error, { markId: markToBeUpdated?.uid });
      }
    }
  };

  // RECTANGLE HIGHLIGHT
  const addRectangleHighlightHandler = ({ comment, tags }: any = {}) => {
    try {
      pauseCreateMarkHandlerRef.current = true;

      const rectangleMark = viewerControl.addMark(
        markToBeCreated.pageNumber,
        Mark.Type.RectangleAnnotation,
      );

      rectangleMark.setRectangle(markToBeCreated.rectangle);
      rectangleMark.setFillColor(REGULAR_COLOR);
      rectangleMark.setData('comment', comment);
      rectangleMark.opacity = 150;
      rectangleMark.interactionMode = Mark.InteractionMode.SelectionDisabled;
      rectangleMark.borderThickness = 1;
      rectangleMark.borderColor = '#000000';

      addAnnotationHandler(rectangleMark, tags);
    } finally {
      setTimeout(() => {
        pauseCreateMarkHandlerRef.current = false;
      }, 500); // needed for acusoft to stop triggering events
    }
  };

  const addRectangleRedactionHandler = ({ reason }: any = {}) => {
    try {
      pauseCreateMarkHandlerRef.current = true;

      const rectangleMark = viewerControl.addMark(
        markToBeCreated.pageNumber,
        Mark.Type.RectangleRedaction,
      );

      rectangleMark.setRectangle(markToBeCreated.rectangle);
      rectangleMark.setData('reason', reason);
      rectangleMark.setReason(reason);
      rectangleMark.interactionMode = Mark.InteractionMode.SelectionDisabled;

      addAnnotationHandler(rectangleMark);
    } finally {
      setTimeout(() => {
        pauseCreateMarkHandlerRef.current = false;
      }, 500); // needed for acusoft to stop triggering events
    }
  };

  const addCrossReferenceHandler = ({ link, linkText, crossRef }: any) => {
    try {
      pauseCreateMarkHandlerRef.current = true;

      const crossRefMark = viewerControl.addMark(
        markToBeCreated.pageNumber,
        Mark.Type.TextAnnotation,
      );
      crossRefMark.setRectangle(markToBeCreated.rectangle);
      crossRefMark.setFontSize(crossRefFontSize);
      crossRefMark.setText(crossRef);
      crossRefMark.setBorderColor('#FFFFFF');
      crossRefMark.setData('link', JSON.stringify(link));
      crossRefMark.setData('linkId', link.id);
      crossRefMark.setData('linkName', link.name);
      crossRefMark.setData('linkType', link.type);
      crossRefMark.setData('linkText', linkText);
      crossRefMark.setData('crossRef', crossRef);
      crossRefMark.setData('href', getHrefFromLink(link));
      crossRefMark.interactionMode = Mark.InteractionMode.SelectionDisabled;

      addAnnotationHandler(crossRefMark);
    } finally {
      setTimeout(() => {
        pauseCreateMarkHandlerRef.current = false;
      }, 500); // needed for acusoft to stop triggering events
    }
  };

  const updateCrossRefferenceHandler = ({ link, linkText, crossRef }: any) => {
    markToBeUpdated.setFillColor('transparent');
    markToBeUpdated.setText(crossRef);
    markToBeUpdated.setData('link', JSON.stringify(link));
    markToBeUpdated.setData('linkId', link.id);
    markToBeUpdated.setData('linkName', link.name);
    markToBeUpdated.setData('linkType', link.type);
    markToBeUpdated.setData('linkText', linkText);
    markToBeUpdated.setData('crossRef', crossRef);
    markToBeUpdated.setData('href', getHrefFromLink(link));

    const annotation = highlights.find((x: any) => x.annotation.uid === markToBeUpdated.uid);

    if (annotation) {
      try {
        // Verify mark is still attached to the document before serializing
        const allMarks = viewerControl.getAllMarks();
        const markExists = allMarks.some((m: any) => m.uid === markToBeUpdated.uid);

        if (!markExists) {
          return;
        }

        const serializationResult = viewerControl.serializeMarks([markToBeUpdated]);

        // Always wrap in Promise.resolve to ensure proper Promise chain
        Promise.resolve(serializationResult)
          .then(([markObject]: any) => {
            if (markToBeUpdated) setMarkToBeUpdated(undefined);
            updateAnnotationHandler({ ...annotation.annotation, ...markObject }, annotation.id);
          })
          .catch((error: any) => {
            console.warn('Error serializing mark for cross reference update:', error, {
              markId: markToBeUpdated.uid,
            });
          });
      } catch (error) {
        console.warn('Error in updateCrossRefferenceHandler:', error, {
          markId: markToBeUpdated?.uid,
        });
      }
    }
  };

  const copyTextHandler = () => {
    if (textSelection) {
      navigator.clipboard.writeText(textSelection);
      setTextSelection(null);
      deleteMarkToBeCreated();
      addToast(T.translate('generic.selectedTextCopyToClipboard'), {
        appearance: 'success',
        autoDismiss: true,
        autoDismissTimeout: 1000,
      });
    }
  };

  const addEventHandler = () => {
    // Only delete the mark if it's a navigation highlight (auto-highlight)
    // Keep manual highlights created via MouseToolSelectButtons
    if (markToBeCreated && markToBeCreated.getData('subType') === 'highlightEvent') {
      deleteMarkToBeCreated();
    }
    // For manual highlights (subType !== 'highlightEvent' or no subType), keep the mark
  };

  const getHrefFromLink = (link: any) => {
    const goToPage = link && link.page && parseInt(link.page);
    const caseIdForHref = link?.linkedCaseId || currentCaseId;
    // If it's a custom link, return as is
    if (link && link.type === CUSTOM_FLAG) {
      return link.id.includes('http') ? link.id : `//${link.id}`;
    }

    // For file links, include all available parameters
    if (link) {
      let url = `/case/${caseIdForHref}/files/${link.id}`;

      // Add query parameters
      const params = new URLSearchParams();

      // Add goToPage parameter if available
      if (goToPage) {
        params.append('goToPage', goToPage.toString());
      }

      // Get tab from link.tab or extract from crossRefPrompt if available
      let tabValue = link.tab;
      if (!tabValue && link.crossRefPrompt) {
        // Extract tab from patterns like "[E/8/45]" or "[E/9]"
        const crossRefMatch = link.crossRefPrompt.match(/\[([^/]+)\/(\d+)(\/|])/);
        if (crossRefMatch && crossRefMatch[2]) {
          tabValue = crossRefMatch[2];
        }
      }

      // Add tab parameter if available
      if (tabValue) {
        params.append('tab', tabValue);
      }

      // Get prefix from link.prefix or extract from crossRefPrompt if available
      let prefixValue = link.prefix;
      if (!prefixValue && link.crossRefPrompt) {
        // Extract prefix from patterns like "[E/8/45]" or "[E/9]"
        const prefixMatch = link.crossRefPrompt.match(/\[([^/]+)\//);
        if (prefixMatch && prefixMatch[1]) {
          prefixValue = prefixMatch[1];
        }
      }

      // Extract startPage from pageRange if available
      let startPage = link.startPage;
      if (!startPage && link.pageRange) {
        // Extract the start page from the pageRange (e.g., "E1069-1264")
        const pageRange = link.pageRange;
        const prefix = prefixValue || '';

        // Remove the prefix from the beginning of the pageRange
        const withoutPrefix = pageRange.startsWith(prefix)
          ? pageRange.substring(prefix.length)
          : pageRange;

        // Extract the start page (number before the dash)
        const dashIndex = withoutPrefix.indexOf('-');
        if (dashIndex > 0) {
          startPage = withoutPrefix.substring(0, dashIndex);
        }
      }

      // Add startPage if available
      if (startPage) {
        params.append('startPage', startPage);
      }

      // Calculate pageCount from pageRange if available
      let pageCount = link.pageCount;
      if (!pageCount && link.pageRange) {
        // Extract the page count from the pageRange (e.g., "E1069-1264")
        const pageRange = link.pageRange;
        const prefix = prefixValue || '';

        // Remove the prefix from the beginning of the pageRange
        const withoutPrefix = pageRange.startsWith(prefix)
          ? pageRange.substring(prefix.length)
          : pageRange;

        // Extract the start and end pages
        const dashIndex = withoutPrefix.indexOf('-');
        if (dashIndex > 0) {
          const startNum = parseInt(withoutPrefix.substring(0, dashIndex));
          const endNum = parseInt(withoutPrefix.substring(dashIndex + 1));

          // Calculate page count
          if (!isNaN(startNum) && !isNaN(endNum)) {
            pageCount = String(endNum - startNum + 1);
          }
        }
      }

      // Add pageCount if available
      if (pageCount) {
        params.append('pageCount', pageCount);
      }

      // Add globalPagePrefix if available
      if (prefixValue) {
        params.append('globalPagePrefix', prefixValue);
      }

      // Only add the query string if we have parameters
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      return url;
    }

    return '';
  };

  const updateAnnotationHandler = useCallback(
    (
      annotation: any,
      id: string,
      tags?: Array<any>,
      shared?: boolean,
      updateImageOnly: boolean = false,
    ) => {
      return updateAnnotationApi({
        annotation,
        tags,
        id,
        shared,
        fileId,
        ...((currentFileMetaData &&
          currentFileMetaData.folderId && {
            folderId: currentFileMetaData.folderId,
          }) ||
          (queryParam &&
            queryParam.folderId && {
              folderId: queryParam.folderId,
            })),
        updateImageOnly,
        // ensure correct slot is updated when in side-by-side compare
        forCompare: !!isFileToCompare,
      });
    },
    [currentFileMetaData, isFileToCompare, fileId, queryParam, updateAnnotationApi],
  );

  const updateImageUnderAnnotation = useCallback(
    ({ annotation, id, tags, shared }: any, mark: any) => {
      if (annotation && mark.type === Mark.Type.RectangleAnnotation) {
        const convertResult = convert(mark, viewerControl);

        if (!convertResult) {
          return;
        }

        const { clientX, clientY, clientWidth, clientHeight } = convertResult;
        html2canvas(document.body, {
          x: clientX,
          y: clientY,
          imageTimeout: 0,
          ignoreElements: (elem) =>
            (elem.className && elem.className.toString().includes('MuiPaper-root')) ||
            elem.nodeName === 'rect',
          allowTaint: true,
          useCORS: true,
        }).then((canvas) => {
          const newCanvas = cropCanvas(canvas, clientWidth, clientHeight);
          if (mark) mark.setData('image', newCanvas.toDataURL());
          if (annotation)
            updateAnnotationHandler(
              {
                ...annotation,
                data: {
                  ...annotation.data,
                  image: newCanvas.toDataURL(),
                },
              },
              id,
              tags,
              shared,
              true,
            );
        });
      }
    },
    [updateAnnotationHandler, viewerControl],
  );

  const addTimelineAnnotationHandler = useCallback(
    (mark: any, timeline: any) => {
      if (!mark) return Promise.resolve(null);
      return new Promise((resolve) => {
        try {
          // Verify mark is still attached to the document before serializing
          const allMarks = viewerControl.getAllMarks();
          const markExists = allMarks.some((m: any) => m.uid === mark.uid);

          if (!markExists) {
            resolve(null);
            return;
          }

          // Set the comment data on the mark before serialization
          if (timeline.comment) {
            mark.setData('comment', timeline.comment);
          }

          const serializationResult = viewerControl.serializeMarks([mark]);
          // Always wrap in Promise.resolve to ensure proper Promise chain
          Promise.resolve(serializationResult)
            .then(([markObject]: any) => {
              // Clean the markObject by removing subType from data if it exists
              const cleanedMarkObject = {
                ...markObject,
                data: markObject.data
                  ? {
                      ...markObject.data,
                      subType: undefined,
                    }
                  : markObject.data,
              };

              // Remove undefined properties to keep the object clean
              if (cleanedMarkObject.data && cleanedMarkObject.data.subType === undefined) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { subType, ...dataWithoutSubType } = cleanedMarkObject.data;
                cleanedMarkObject.data = dataWithoutSubType;
              }

              addAnnotationApi({
                annotation: cleanedMarkObject,
                timeline,
                fileId,
                ...((currentFileMetaData?.folderId && {
                  folderId: currentFileMetaData.folderId,
                }) ||
                  (queryParam?.folderId && {
                    folderId: queryParam.folderId,
                  })),
                forCompare: !!isFileToCompare,
              }).then((annotation: any) => {
                resolve(annotation);
                updateImageUnderAnnotation(annotation, mark);
              });
            })
            .catch(() => {
              resolve(null);
            });
        } catch {
          resolve(null);
        }
      });
    },
    [
      addAnnotationApi,
      currentFileMetaData.folderId,
      fileId,
      isFileToCompare,
      queryParam.folderId,
      updateImageUnderAnnotation,
      viewerControl,
    ],
  );

  const addAnnotationHandler = useCallback(
    (mark: any, tags?: any) => {
      if (!mark) return;

      try {
        // Verify mark is still attached to the document before serializing
        const allMarks = viewerControl.getAllMarks();
        const markExists = allMarks.some((m: any) => m.uid === mark.uid);

        if (!markExists) {
          return;
        }

        const serializationResult = viewerControl.serializeMarks([mark]);

        // Always wrap in Promise.resolve to ensure proper Promise chain
        Promise.resolve(serializationResult)
          .then(([markObject]: any) => {
            addAnnotationApi({
              annotation: markObject,
              tags,
              fileId,
              ...((currentFileMetaData?.folderId && {
                folderId: currentFileMetaData.folderId,
              }) ||
                (queryParam?.folderId && {
                  folderId: queryParam.folderId,
                })),
              // ensure correct slot is updated when in side-by-side compare
              forCompare: !!isFileToCompare,
            }).then((annotation: any) => {
              updateImageUnderAnnotation(annotation, mark);
            });
            deleteMarkToBeCreated(mark);
          })
          .catch((error: any) => {
            console.warn('Error serializing mark:', error, { markId: mark.uid });
          });
      } catch (error) {
        console.warn('Error in addAnnotationHandler:', error, { markId: mark?.uid });
      }
    },
    [
      addAnnotationApi,
      currentFileMetaData.folderId,
      deleteMarkToBeCreated,
      fileId,
      isFileToCompare,
      queryParam.folderId,
      updateImageUnderAnnotation,
      viewerControl,
    ],
  );

  const deleteAnnotationHandler = (mark: any) => {
    const annotation = highlights.find((x: any) => x.annotation.uid === mark.uid);
    deleteAnnotationApi({ annotation, fileId }).then(() => {
      try {
        // Verify mark is still attached to the document before deleting
        const allMarks = viewerControl.getAllMarks();
        const markExists = allMarks.some((m: any) => m.uid === mark.uid);

        if (markExists) {
          viewerControl.deleteMarks([mark]);
        }
      } catch (error) {
        console.warn('Error deleting mark:', error, { markId: mark?.uid });
      }
    });
  };

  const textSelectedHandler = ({ selectedText: textSel }: any) => {
    setTextSelection(textSel);
    if (copyable && textSel && textSel.trim()) {
      navigator.clipboard
        .writeText(textSel)
        .then(() => {
          addToast(T.translate('generic.selectedTextCopyToClipboard'), {
            appearance: 'success',
            autoDismiss: true,
            autoDismissTimeout: 1000,
          });
        })
        .catch(() => {
          addToast(T.translate('generic.selectedTextCopyToClipboardError'), {
            appearance: 'error',
            autoDismiss: true,
            autoDismissTimeout: 1000,
          });
        });
    }
  };

  const updateMousePosition = ({ /*mark,*/ clientX, clientY }: any) => {
    currentPointRef.current = { x: clientX, y: clientY };
    // const { clientWidth, clientX, clientY } = convert(mark, viewerControl);
    // const x = clientX + clientWidth / 2 + clientWidth; // add client width so it's similar to mark button
    // const y = clientY - 5;
    // currentPoint = { x, y };
  };

  const clearTooltipTimeout = () => {
    if (timeoutIdRef.current) {
      timeoutIdRef.current = clearTimeout(timeoutIdRef.current);
    }
  };

  const showTooltipHandler = useCallback(
    (mark: typeof Mark) => {
      if (mark.type !== Mark.Type.TextHyperlinkAnnotation) return; // show only for links
      if (markToBeCreated || markToBeUpdated) return; // don't allow show tooltip if mark button is active
      if (hideTimeoutRef.current) hideTimeoutRef.current = clearTimeout(hideTimeoutRef.current);
      if (tooltipPoint !== currentPointRef.current || markHoveredOver !== mark) {
        setTooltipPoint(currentPointRef.current);
        setMarkHoveredOver(mark);
      }
    },
    [
      hideTimeoutRef,
      markHoveredOver,
      markToBeCreated,
      markToBeUpdated,
      setMarkHoveredOver,
      tooltipPoint,
    ],
  );

  const markMouseEnterHandler = useCallback(
    (eventArgs: any) => {
      if (isPresenting) return;

      if (eventArgs.mark && eventArgs.mark.type === Mark.Type.TextHyperlinkAnnotation) {
        (document.querySelector(`[data-pcc-mark='mark-${eventArgs.mark.id}']`) as any).style =
          'cursor: pointer';
      }

      const compareMousePoints = (eArgs: any) => {
        // distance to travel in px
        const travelDistance = 6;
        const { x: prevX, y: prevY } = previousPointRef.current || {};
        const { x, y } = currentPointRef.current || {};
        // Check if the mouse has slowed down enough
        if (Math.sqrt((prevX - x) * (prevX - x) + (prevY - y) * (prevY - y)) < travelDistance) {
          viewerControl.off('MarkMouseOver', updateMousePosition);
          // Clear old data
          timeoutIdRef.current = previousPointRef.current = null;
          showTooltipHandler(eArgs.mark);
        } else {
          // Update the previous mouse position for the next comparison
          previousPointRef.current = currentPointRef.current;
          timeoutIdRef.current = setTimeout(() => {
            compareMousePoints(eArgs);
          }, 250);
        }
      };

      if (eventArgs.mark === markToBeCreated) return;
      // if they have something to show
      if (!(eventArgs.mark.getData('comment') || eventArgs.mark.getData('linkId'))) return;
      if (hideTimeoutRef.current && markHoveredOver === eventArgs.mark) {
        hideTimeoutRef.current = clearTimeout(hideTimeoutRef.current);
        return;
      }

      // Clear existing timeouts
      clearTooltipTimeout();
      // Clone event so it can be used in IE asynchronously
      const ev = Object.assign(eventArgs);
      // Set previous X and Y position based on initial entry point
      previousPointRef.current = { x: ev.clientX, y: ev.clientY };
      // Update current X and Y every time the mouse moves
      viewerControl.on('MarkMouseOver', updateMousePosition);
      timeoutIdRef.current = setTimeout(() => {
        compareMousePoints(ev);
      }, 250);
    },
    [
      hideTimeoutRef,
      isPresenting,
      markHoveredOver,
      markToBeCreated,
      showTooltipHandler,
      viewerControl,
    ],
  );

  const hideTooltipHandler = useCallback(
    (delay = 0) => {
      hideTimeoutRef.current = setTimeout(() => {
        if (markHoveredOver) {
          setMarkHoveredOver(null);
        }
      }, delay);
    },
    [hideTimeoutRef, markHoveredOver, setMarkHoveredOver],
  );

  const markMouseLeaveHandler = useCallback(() => {
    // Clear existing timeouts
    clearTooltipTimeout();

    if (!markHoveredOver) {
      return;
    }

    // Remove the expensive event listener
    viewerControl.off('MarkMouseOver', updateMousePosition);
    hideTooltipHandler(500);
  }, [hideTooltipHandler, markHoveredOver, viewerControl]);

  useEffect(() => {
    viewerControl.on('TextSelected', textSelectedHandler);
    viewerControl.on('MarkMouseEnter', markMouseEnterHandler);
    viewerControl.on('MarkMouseLeave', markMouseLeaveHandler);
    viewerControl.on('MarkChanged', hideTooltipHandler);
    viewerControl.on('MarkRemoved', hideTooltipHandler);
    scrollContainer.addEventListener('scroll', hideTooltipHandler);

    return () => {
      viewerControl.off('TextSelected', textSelectedHandler);
      viewerControl.off('MarkMouseEnter', markMouseEnterHandler);
      viewerControl.off('MarkMouseLeave', markMouseLeaveHandler);
      viewerControl.off('MarkChanged', hideTooltipHandler);
      viewerControl.off('MarkRemoved', hideTooltipHandler);
      scrollContainer.removeEventListener('scroll', hideTooltipHandler);
    };
  }, [
    hideTooltipHandler,
    markMouseEnterHandler,
    markMouseLeaveHandler,
    scrollContainer,
    viewerControl,
  ]);

  return (
    <>
      <MarkButton
        addHighlight={addHighlightHandler}
        addRedaction={addRedactionHandler}
        addLink={addLinkHandler}
        copyText={copyTextHandler}
        addRectangleHighlight={addRectangleHighlightHandler}
        addRectangleRedaction={addRectangleRedactionHandler}
        addTimelineAnnotationHandler={addTimelineAnnotationHandler}
        addCrossReference={addCrossReferenceHandler}
        updateAnnotation={updateAnnotationHandler}
        markToBeCreated={markToBeCreated}
        markToBeUpdated={markToBeUpdated}
        viewerControl={viewerControl}
        scrollContainer={scrollContainer}
        highlights={highlights}
        updateHighlight={updateHighlightHandler}
        updateRedaction={updateRedactionHandler}
        updateCrossRefference={updateCrossRefferenceHandler}
        updateLink={updateLinkHandler}
        deleteAnnotation={deleteAnnotationHandler}
        canCreateAndEditCrossRefs={canCreateAndEditCrossRefs}
        canCreateAndEditPublicHyperlinks={canCreateAndEditPublicHyperlinks}
        addEventHandler={addEventHandler}
        fileId={fileId}
        copyable={copyable}
      />

      <MarkTooltip
        markHoveredOver={markHoveredOver}
        tooltipPoint={tooltipPoint}
        showTooltip={showTooltipHandler}
        hideTooltip={hideTooltipHandler}
        canCreateAndEditPublicHyperlinks={canCreateAndEditPublicHyperlinks}
        setMarkToBeUpdated={setMarkToBeUpdated}
        currentLinkData={currentLinkData}
      />
    </>
  );
};
